/**
 * Serviço de Dashboards
 *
 * Gerencia dados agregados para dashboards usando RPC functions.
 * Multitenant: passa p_negocio_id (perfil /me) para exibir só dados do tenant atual.
 */

import { postgrestClient } from "../lib/postgrest/client";
import { apiServiceClient } from "../config/api";
import { registrarFalha } from "../lib/telemetria";

/**
 * Serviço de dashboards
 */
export const dashboardsService = {
  /**
   * Busca dados de frequência agregados (RPC function)
   * @param {Object} params
   * @param {string} params.dataInicio
   * @param {string} params.dataFim
   * @param {string} params.turmaId
   * @param {string} params.nucleoId
   * @param {number} params.anoId
   * @returns {Promise<Object>}
   */
  async getFrequencia(params = {}) {
    let dataInicio = params.dataInicio;
    let dataFim = params.dataFim;

    // Se não fornecer datas, calcula baseado no período
    if (!dataInicio || !dataFim) {
      const hoje = new Date();

      if (params.periodo === "semanal") {
        // Últimos 7 dias
        dataFim = hoje.toISOString().split('T')[0];
        const inicio = new Date(hoje);
        inicio.setDate(inicio.getDate() - 7);
        dataInicio = inicio.toISOString().split('T')[0];
      } else if (params.periodo === "mensal") {
        // Mês atual
        dataFim = hoje.toISOString().split('T')[0];
        dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
      } else if (params.periodo === "trimestral") {
        // Últimos 3 meses
        dataFim = hoje.toISOString().split('T')[0];
        const inicio = new Date(hoje);
        inicio.setMonth(inicio.getMonth() - 3);
        dataInicio = inicio.toISOString().split('T')[0];
      } else {
        // Padrão: últimos 30 dias
        dataFim = hoje.toISOString().split('T')[0];
        const inicio = new Date(hoje);
        inicio.setDate(inicio.getDate() - 30);
        dataInicio = inicio.toISOString().split('T')[0];
      }
    }

    return postgrestClient.rpc("dashboard_frequencia", {
      p_data_inicio: dataInicio,
      p_data_fim: dataFim,
      p_turma_id: params.turmaId || null,
      p_nucleo_id: params.nucleoId || null,
      p_ano_id: params.anoId ? parseInt(params.anoId) : null,
    });
  },

  /**
   * Busca dados de capacidade agregados (RPC function)
   * @param {Object} params
   * @param {string} params.nucleoId
   * @param {number} params.anoId
   * @param {number} params.usuarioId - ID do usuário para filtrar por núcleos (opcional)
   * @param {string} params.userRole - Role do usuário (ADMIN e ADMINISTRATIVO vêem todos)
   * @returns {Promise<Object>}
   */
  async getCapacidade(params = {}) {
    const nucleoId = params.nucleoId || null;
    const anoId = params.anoId ? parseInt(params.anoId) : null;
    if (params.userRole === "ADMIN" || params.userRole === "ADMINISTRATIVO" || params.userRole === "TECNICO_GERAL" || params.userRole === "TECNICO" || !params.usuarioId) {
      // A versão (p_nucleo_id, p_negocio_id) referenciava matricula_pilar e foi
      // removida (Alembic 0003). Admins veem o total do sistema (doc 08, S18).
      return postgrestClient.rpc("dashboard_capacidade", { p_ano_id: anoId, p_nucleo_id: nucleoId });
    }
    return postgrestClient.rpc("dashboard_capacidade_por_usuario", {
      p_usuario_id: parseInt(params.usuarioId),
      p_ano_id: anoId,
      p_nucleo_id: nucleoId,
    });
  },

  /**
   * Busca dados de proficiência agregados (RPC function)
   * @param {Object} params
   * @param {string} params.anoId
   * @param {string} params.turmaId
   * @returns {Promise<Object>}
   */
  async getProficiencia(params = {}) {
    return postgrestClient.rpc("dashboard_proficiencia", {
      p_ano_id: params.anoId ? parseInt(params.anoId) : null,
      p_turma_id: params.turmaId || null,
    });
  },

  /**
   * Busca dados de modalidades agregados (RPC function)
   * @param {Object} params
   * @param {string} params.modalidadeId
   * @param {string} params.nucleoId
   * @param {number} params.anoId
   * @param {number} params.usuarioId - ID do usuário para filtrar por núcleos (opcional)
   * @param {string} params.userRole - Role do usuário (ADMIN e ADMINISTRATIVO vêem todos)
   * @returns {Promise<Object>}
   */
  async getModalidades(params = {}) {
    const modalidadeId = params.modalidadeId || null;
    const nucleoId = params.nucleoId || null;
    const anoId = params.anoId ? parseInt(params.anoId) : null;
    // A versão com p_negocio_id referenciava a tabela "modalidades", que não
    // existe, e foi removida (Alembic 0003).
    const common = { p_modalidade_id: modalidadeId, p_nucleo_id: nucleoId, p_ano_id: anoId };
    if (params.userRole === "ADMIN" || params.userRole === "ADMINISTRATIVO" || params.userRole === "TECNICO_GERAL" || params.userRole === "TECNICO" || !params.usuarioId) {
      return postgrestClient.rpc("dashboard_modalidades", common);
    }
    return postgrestClient.rpc("dashboard_modalidades_por_usuario", {
      p_usuario_id: parseInt(params.usuarioId),
      p_modalidade_id: modalidadeId,
      p_nucleo_id: nucleoId,
      p_ano_id: anoId,
    });
  },

  /**
   * Busca dados unificados de capacidade e atividades do tenant.
   * Usa /v1/dashboards/indicadores (backend novo, Fase 3); o tenant é o do
   * usuário — negocioId só escolhe o tenant para o TECNICO.
   * Se a rota falhar, cai na RPC antiga, com a falha registrada.
   * @param {Object} params
   * @param {string} params.negocioId - UUID do tenant para filtrar (obrigatório)
   * @returns {Promise<Object>} Objeto com estatisticas, ocupacaoPorNucleo, ocupacaoPorSala e modalidades
   */
  async getIndicadores(params = {}) {
    if (!params.negocioId) {
      throw new Error("negocioId é obrigatório para getIndicadores");
    }

    try {
      const query = new URLSearchParams({ negocio_id: params.negocioId });
      if (params.nucleoId) query.append("nucleo_id", params.nucleoId);
      if (params.anoId) query.append("ano_id", params.anoId);
      const response = await apiServiceClient.request(
        `/v1/dashboards/indicadores?${query.toString()}`,
        { method: "GET" }
      );
      const indicadores = response?.data || response;
      if (indicadores && indicadores.estatisticas) {
        return indicadores;
      }
      throw new Error("Resposta inesperada de /v1/dashboards/indicadores");
    } catch (error) {
      // 403 é recusa (tenant diferente da sessão), não falha do caminho novo.
      if (error?.status === 403) throw error;
      registrarFalha("dashboards.indicadores", error);
      return postgrestClient.rpc("dashboard_indicadores", {
        p_nucleo_id: params.nucleoId || null,
        p_ano_id: params.anoId ? parseInt(params.anoId) : null,
        p_negocio_id: params.negocioId || null,
      });
    }
  },

  /**
   * Busca dados de distribuição por escola de origem (RPC function)
   * @param {Object} params
   * @param {number} params.anoId
   * @returns {Promise<Array>}
   */
  async getEscolasOrigem(params = {}) {
    return postgrestClient.rpc("dashboard_escolas_origem", {
      p_ano_id: params.anoId ? parseInt(params.anoId) : null,
      p_negocio_id: params.negocioId || null,
    });
  },

  /**
   * Alunos ativos no mês por núcleo: quantos alunos o relatório de Alunos
   * Frequentes listaria em cada núcleo, no mês, com pelo menos 1 presença.
   * Só existe no backend novo (sem RPC de fallback); núcleos sem nenhum aluno
   * ativo não vêm na resposta.
   * @param {{ano: number, mes: number, negocioId?: string, nucleoId?: string}} params
   * @returns {Promise<Array<{nucleo_id: string, nucleo_nome: string, alunos_ativos: number}>>}
   */
  async getAlunosAtivosPorNucleo({ ano, mes, negocioId = null, nucleoId = null }) {
    const query = new URLSearchParams({ ano: String(ano), mes: String(mes) });
    if (negocioId) query.append("negocio_id", negocioId);
    if (nucleoId) query.append("nucleo_id", nucleoId);
    const response = await apiServiceClient.request(`/v1/dashboards/alunos-ativos?${query.toString()}`, { method: "GET" });
    const linhas = response?.data || response;
    if (!Array.isArray(linhas)) {
      throw new Error("Resposta inesperada de /v1/dashboards/alunos-ativos");
    }
    return linhas;
  },

  async getAlunosAtivosTotal({ ano, mes, negocioId = null, nucleoId = null }) {
    const query = new URLSearchParams({ ano: String(ano), mes: String(mes) });
    if (negocioId) query.append("negocio_id", negocioId);
    if (nucleoId) query.append("nucleo_id", nucleoId);
    const response = await apiServiceClient.request(`/v1/dashboards/alunos-ativos/total?${query.toString()}`, { method: "GET" });
    const total = response?.data || response;
    if (typeof total?.alunos_ativos !== "number") {
      throw new Error("Resposta inesperada de /v1/dashboards/alunos-ativos/total");
    }
    return total;
  },
};

export default dashboardsService;

